import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  GlobalConfiguration,
  PaginationConfiguration,
} from "../../../../utils/configurationProvider";
import { toast } from "react-toastify";
import NotificationBell from "../../../../components/Notification/NotificationBell";
import CommonToast from "../../../../components/_utils/CommonToast";
import ICONS from "../../../../assets/Icons";
import {
  getAllFieldForceAccount,
  resetToggleFfa,
} from "../../../../redux/features/accounts/fieldForceAccount";
import SecondaryLargeButton from "../../../../components/_utils/CommonButtons/SecondaryLargeButton/SecondaryLargeButton";
import TertiaryTopRoundButton from "../../../../components/_utils/CommonButtons/TertiaryTopRoundButton/TertiaryTopRoundButton";
import CommonCardDashboard from "../../../../components/_utils/CommonCardDashboard";
import {
  disableSwitcher,
  enableSwitcher,
} from "../../../../redux/features/multiEntitySwitcher/multiEntitySwitcher";
import {
  getEntities,
  updateEntities,
} from "../../../../redux/features/Settings/Management/entities";
import { resetIsCreateAcc } from "../../../../redux/features/verifyPAN/verifyPAN";
import useInitials from "../../../../utils/useInitials";
import {
  renderWidget,
  WIDGET_IDS,
  WIDGET_TITLES,
} from "../../../HomeDashboard/Widgets/WidgetRegistry.jsx";
import { toCamelCase } from "../../../../utils/toCamelCase.js";
import globalText from "../../../../utils/globalText.js";

const FieldForceAccountDashboard = (props) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // Tracks which statistics slide is currently selected.
  const [currentSlide, setCurrentSlide] = useState(
    GlobalConfiguration.lengthCheckZero,
  );
  const [amt, setAmt] = useState("1000");
  const [amount, setAmount] = useState("1000");
  const initials = useInitials();

  const { allfieldForceAccountData, allfieldForceAccountLoading } = useSelector(
    (state) => state.fieldForceAccount,
  );
  const userData = useSelector((state) => state.refreshToken.data);

  const MAX_LIMIT = 100000;

  const [transactionLimitModal, setTransactionLimitModal] = useState(false);
  const services = useSelector((state) => state.refreshToken.services) ?? [];

  const { markerData } = useSelector((s) => s.multiEntitySwitcherSlice);
  // Available statistics slides.
  const slideRefs = useRef([]);
  const [containerHeight, setContainerHeight] = useState(
    globalText?.AUTO.toLowerCase(),
  );
  const [visitedSlides, setVisitedSlides] = useState(
    new Set([GlobalConfiguration.lengthCheckZero]),
  );

  const statisticsSlides = useMemo(
    () => [{ widget_id: WIDGET_IDS.ACCOUNTS_PER_DAY }],
    [],
  );

  const dynamicTitle =
    WIDGET_TITLES[statisticsSlides[currentSlide]?.widget_id] ||
    globalText?.STATISTICS;

  const totalCount =
    allfieldForceAccountData?.total_count ??
    GlobalConfiguration?.lengthCheckZero;

  const isViewAllDisabled =
    allfieldForceAccountLoading ||
    totalCount === GlobalConfiguration?.lengthCheckZero;

  // Advances the statistics carousel when another slide is available.
  const nextSlide = () => {
    if (
      currentSlide <
      statisticsSlides.length - GlobalConfiguration?.lengthCheckOne
    ) {
      setCurrentSlide(currentSlide + GlobalConfiguration?.lengthCheckOne);
    }
  };
  // Moves the statistics carousel back when it is not on the first slide.
  const prevSlide = () => {
    if (currentSlide > GlobalConfiguration?.lengthCheckZero) {
      setCurrentSlide(currentSlide - GlobalConfiguration?.lengthCheckOne);
    }
  };

  const handleAmountChange = (e) => {
    const rawValue = e.target.value.replace(/,/g, "");

    if (!/^\d*$/.test(rawValue)) return;

    if (rawValue === "") {
      setAmount("");
      return;
    }

    if (rawValue.length > 15) return;

    const numericValue = Number(rawValue);

    if (numericValue > MAX_LIMIT) {
      toast.dismiss();
      CommonToast({
        text: "The amount cannot exceed INR1,00,000",
        icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
        variant: "var(--error)",
      });
      return; // do NOT update state
    }

    setAmount(rawValue);
  };

  const onMount = () => {
    dispatch(getAllFieldForceAccount({}));
  };

  useEffect(() => {
    dispatch(enableSwitcher());
    return () => {
      dispatch(disableSwitcher());
    };
  }, [dispatch, props?.internalAccount]);

  useEffect(() => {
    onMount();
  }, [markerData]);

  // Update visited widget slides set whenever the active slide changes
  useEffect(() => {
    if (statisticsSlides.length > GlobalConfiguration.lengthCheckZero) {
      setVisitedSlides((prev) => new Set(prev).add(currentSlide));
    }
  }, [currentSlide, statisticsSlides.length]);

  useEffect(() => {
    const activeSlideWrapper = slideRefs.current[currentSlide];
    if (!activeSlideWrapper) return;

    const contentElement =
      activeSlideWrapper.firstElementChild || activeSlideWrapper;

    const updateHeight = () => {
      if (contentElement) {
        // Get exact height of the rendered widget content
        const height = contentElement.getBoundingClientRect().height;
        if (height > GlobalConfiguration.lengthCheckZero) {
          setContainerHeight(`${height}px`);
        }
      }
    };

    updateHeight();

    const resizeObserver = new ResizeObserver(() => {
      updateHeight();
    });

    resizeObserver.observe(contentElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, [currentSlide, statisticsSlides, visitedSlides]);

  const handleDatacardClick = (approvalStatus) => {
    const routeMap = {
      approved: "/field_force_account_dashboard/field_force_account_view_all",
      pending:
        services.includes("FFAAccount") || services?.includes("Admin")
          ? "/field_force_account_dashboard/field_force_account_approval"
          : (services.includes("FFVAccount") || services?.includes("Admin")) &&
              !services.includes("FFAAccount")
            ? "/field_force_account_dashboard/field_force_account_view_all"
            : navigate("/feature_not_assigned", {
                state: {
                  unavailableService: "FFAAccount",
                },
              }),
    };

    dispatch(resetToggleFfa());
    navigate(routeMap[approvalStatus], {
      state: { approvalStatus },
    });
  };

  const approvedValue =
    allfieldForceAccountData?.summary?.approved_accounts_count === undefined
      ? GlobalConfiguration.dashboardPrimaryCount
      : allfieldForceAccountData?.summary?.approved_accounts_count;

  const pendingValue =
    allfieldForceAccountData?.summary?.pending_accounts_count === undefined
      ? GlobalConfiguration.dashboardPrimaryCount
      : allfieldForceAccountData?.summary?.pending_accounts_count;

  const accountTypesSummary =
    allfieldForceAccountData && allfieldForceAccountData.summary
      ? Object.entries(allfieldForceAccountData.summary)
      : [];

  const order = [
    "pending_accounts_count",
    "approved_accounts_count",
    //  "rejected_accounts_count",
    //  "blocked_accounts_count",
    //  "archived_accounts_count",
  ];

  const [hasScroll, setHasScroll] = useState(false);
  const divRef = useRef(null);

  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);

  const formatWithCommas = (value) => {
    if (!value) return "";
    return new Intl.NumberFormat("en-IN").format(value);
  };

  const removeCommas = (value) => value.replace(/,/g, "");

  const checkScroll = () => {
    if (divRef.current) {
      const hasVerticalScroll =
        divRef.current.scrollHeight > divRef.current.clientHeight;
      const hasHorizontalScroll =
        divRef.current.scrollWidth > divRef.current.clientWidth;
      setHasScroll(hasVerticalScroll || hasHorizontalScroll);

      const atStart =
        divRef.current.scrollLeft === GlobalConfiguration.lengthCheckZero;
      const atEnd =
        Math.ceil(divRef.current.scrollLeft + divRef.current.clientWidth) >=
        divRef.current.scrollWidth;
      setIsAtStart(atStart);
      setIsAtEnd(atEnd);
    }
  };

  const isUpdateDisabled =
    !amount || Number(amount) === GlobalConfiguration.lengthCheckZero;

  const handleUpdate = async () => {
    const payload = {
      entity_id: id,
      field_force_limit: amount,
    };

    let response = await dispatch(updateEntities(payload));

    if (response?.payload) {
      setAmt(amount);
      setTransactionLimitModal(false);
      dispatch(getEntities({ page_no: 1, page_size: 10 }));
    } else {
      setTransactionLimitModal(false);
    }
  };

  useEffect(() => {
    checkScroll();
    window.addEventListener("resize", checkScroll);
    return () => {
      window.removeEventListener("resize", checkScroll);
    };
  }, [accountTypesSummary]);
  return (
    <>
      <div className=" internalDashboardView bg-primary-bg w-width-full overflow-y-auto px-padding-base pb-padding-md h-screen ">
        <div className="Qsmd:h-navbar-breadcrumb-height"></div>
        <div className="flex justify-center px-padding-md">
          <div className="flex gap-x-gap-md flex-col mt-margin-md pb-padding-xl Qlg:pb-padding-base max-w-tab-width-normal w-width-full Qlg:flex-row mb-margin-xl Qlg:mb-margin-base Qlg:relative">
            <div className="Qlg:w-dashboard-left-card-width">
              <div className="w-width-full flex flex-col pt-padding-md Qsmd:pt-padding-base mt-margin-xl Qsmd:mt-margin-base ">
                <div className="Qsmd:hidden w-width-full fixed top-top-base left-left-base right-right-base bg-text-primary flex items-center justify-between z-z-index-fixed-ui rounded-b-border-radius-sm h-[var(--home-nav-mobile-height)] pl-mobile-padding-l pr-mobile-padding-l border-b border-primary-bg shadow-sm">
                  <div className="flex items-center justify-between">
                    <img
                      src={ICONS?.XPENTRA_BLUE}
                      alt="Xpentra Logo"
                      className="w-xpentra-logo-width h-xpentra-logo-height"
                    />
                  </div>
                  <div className="flex gap-gap-sm items-center">
                    <NotificationBell />
                    <div className="w-px h-5 bg-primary-card mx-2"></div>
                    <div
                      className="text-xs bg-text-secondary text-primary-bg h-[var(--profile-initials-w-h)] w-[var(--profile-initials-w-h)] flex items-center justify-center rounded-full font-semibold cursor-pointer"
                      onClick={() => navigate("/home/profile_quick_menu")}
                    >
                      {initials}
                    </div>
                  </div>
                </div>
                <div className="w-width-full bg-primary-nav flex flex-col sm:flex-row rounded-border-radius-md items-center z-z-index-header gap-2">
                  <div className="w-width-full flex">
                    <SecondaryLargeButton
                      btnText="Create field force account"
                      btnType="button"
                      handleFunction={() => {
                        const requiredService = "FFUAccount";
                        if (services?.includes(requiredService)) {
                          navigate(
                            "/field_force_account_dashboard/create_field_force_account",
                          );
                          // toast.info("Feature coming soon", toast_position);
                          // dispatch(resetServices());

                          dispatch(resetIsCreateAcc());
                        } else {
                          navigate("/feature_not_assigned", {
                            state: { unavailableService: requiredService },
                          });
                        }
                      }}
                      enabledTextColor="var(--text-primary)"
                      disabledTextColor="var(--text-secondary)"
                      enabledBgColor="var(--primary-nav)"
                      hoverOption="white"
                      isLoaderNeeded={false}
                      isLoading={false}
                      icon={ICONS?.ADD_BUTTON_WHITE}
                      imgColor="var(--text-primary)"
                      iconAlt="add_button_white"
                      isDisabled={
                        !(
                          services?.includes("FFUAccount") ||
                          services?.includes("Admin")
                        )
                      }
                    />
                  </div>
                </div>

                <div className="w-width-full rounded-border-radius-md relative overflow-hidden mt-margin-md">
                  <h2 className="bg-primary-card flex items-center justify-between pl-padding-md transition-all duration-animation-medium">
                    <div className="flex items-center gap-gap-sm">
                      <img
                        src={ICONS?.APPROVAL_STATUS}
                        alt="transaction header"
                        className="w-icon-sm"
                      />
                      <p className="text-font-sm font-semibold font-sans text-text-secondary">
                        Account statuses
                      </p>
                    </div>
                    <div className="flex">
                      <div className="border-dividing"></div>
                      <TertiaryTopRoundButton
                        btnText="View all"
                        icon={ICONS?.ARROW_RIGHT_WHITE}
                        imgColor="var(--text-primary)"
                        btnType="button"
                        hoverOption="white"
                        handleFunction={async () => {
                          await dispatch(resetToggleFfa());
                          if (
                            allfieldForceAccountData?.summary
                              ?.pending_accounts_count ??
                            false
                          ) {
                            navigate(
                              "/field_force_account_dashboard/field_force_account_view_all",
                            );
                          }

                          // toast.info("Feature coming soon", toast_position);
                        }}
                        // handleFunction={() => {}}
                        isDisabled={isViewAllDisabled}
                        enabledTextColor={"var(--text-primary)"}
                        disabledTextColor={"var(--text-secondary)"}
                        enabledBgColor={"var(--primary-card)"}
                        disabledBgColor={"var(--primary-card)"}
                      />
                    </div>
                  </h2>
                  <div>
                    <div className="flex flex-col Qsmd:flex-row gap-gap-base p-padding-base bg-primary-nav  ">
                      <CommonCardDashboard
                        label={
                          services?.includes("FFAAccount") ||
                          services?.includes("Admin")
                            ? "Require approval"
                            : "Pending accounts"
                        }
                        count={pendingValue ?? "00"}
                        color="var(--pending)"
                        loader={allfieldForceAccountLoading}
                        onClick={() => handleDatacardClick("pending")}
                      />
                      <div className="border-border border-primary-bg Qsmd:my-margin-md my-margin-base "></div>

                      <CommonCardDashboard
                        label="Approved accounts"
                        count={approvedValue}
                        color="var(--awaiting)"
                        loader={allfieldForceAccountLoading}
                        onClick={() => handleDatacardClick("approved")}
                      />
                    </div>
                  </div>
                </div>

                <div className="w-width-full mt-margin-md bg-primary-nav rounded-border-radius-md">
                  <h2 className="bg-primary-card flex items-center justify-between px-padding-md py-padding-sm text-font-sm font-semibold text-text-secondary transition-all duration-animation-fast rounded-t-border-radius-md">
                    <span className="flex items-center">
                      <img
                        src={ICONS?.STATISTICS}
                        alt={globalText?.STATISTICS + globalText?.HEADER}
                        className="w-icon-sm h-icon-sm mr-margin-sm"
                      />
                      {statisticsSlides.length >
                      GlobalConfiguration.lengthCheckZero
                        ? dynamicTitle
                        : globalText?.STATISTICS}
                    </span>

                    {statisticsSlides.length >
                      GlobalConfiguration.lengthCheckOne && (
                      <div className="w-icon-lg flex items-center gap-gap-xs">
                        <div
                          onClick={prevSlide}
                          className={`cursor-pointer w-icon-sm h-icon-sm ${
                            currentSlide === GlobalConfiguration.lengthCheckZero
                              ? "opacity-light-opacity cursor-not-allowed"
                              : ""
                          }`}
                          style={{
                            pointerEvents:
                              currentSlide ===
                              GlobalConfiguration.lengthCheckZero
                                ? globalText?.NONE?.toLowerCase()
                                : globalText?.AUTO?.toLowerCase(),
                          }}
                        >
                          <img
                            src={ICONS?.ARROW_LEFT_WHITE_CIRCLE}
                            alt={globalText?.PREVIOUS}
                          />
                        </div>
                        <div
                          onClick={nextSlide}
                          className={`cursor-pointer w-icon-sm h-icon-sm ${
                            currentSlide ===
                            statisticsSlides.length -
                              GlobalConfiguration.lengthCheckOne
                              ? "opacity-light-opacity cursor-not-allowed"
                              : ""
                          }`}
                          style={{
                            pointerEvents:
                              currentSlide ===
                              statisticsSlides.length -
                                GlobalConfiguration.lengthCheckOne
                                ? globalText?.NONE?.toLowerCase()
                                : globalText?.AUTO?.toLowerCase(),
                          }}
                        >
                          <img
                            src={ICONS?.ARROW_RIGHT_WHITE_CIRCLE}
                            alt={globalText?.NEXT}
                          />
                        </div>
                      </div>
                    )}
                  </h2>

                  <div
                    className="relative w-width-full duration-animation-fast ease-in-out"
                    style={{ height: containerHeight }}
                  >
                    <div className="relative w-width-full overflow-x-clip">
                      {statisticsSlides.length >
                      GlobalConfiguration.lengthCheckZero ? (
                        <div
                          className="flex item-start transition-transform duration-animation-fast ease-in-out"
                          style={{
                            transform: `translateX(-${currentSlide * PaginationConfiguration.paginationBig}%)`,
                          }}
                        >
                          {statisticsSlides.map((slide, index) => (
                            <div
                              key={slide.widget_id}
                              ref={(el) => (slideRefs.current[index] = el)}
                              className={`w-width-full flex-shrink-0 ${
                                index === currentSlide ? "" : "overflow-hidden"
                              }`}
                              style={{ height: containerHeight }}
                            >
                              {/* LAZY LOAD: Render component ONLY if slide index was visited to prevent extra api calls for non-visited slides */}
                              {visitedSlides.has(index) ? (
                                renderWidget(
                                  slide,
                                  toCamelCase(globalText?.FIELD_FORCE) +
                                    globalText?.ACCOUNTS +
                                    globalText?.DASHBOARD,
                                )
                              ) : (
                                <div className="w-width-full bg-primary-card rounded-b-border-radius-md h-[var(--remindercard-max-height)]">
                                  {" "}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center lg:justify-start h-full text-center p-padding-md">
                          <div className="flex flex-col justify-center items-center w-width-full gap-gap-sm">
                            <img
                              src={ICONS?.NO_STATISTICS_AVAILABLE}
                              alt="No Slides"
                            />
                            <p className="text-font-xs text-text-primary">
                              {globalText?.NO_STATISTICS_AVAILABLE?.split(" ")
                                .slice(
                                  GlobalConfiguration?.lengthCheckZero,
                                  -GlobalConfiguration?.lengthCheckSeven,
                                )
                                .join(" ")}
                              <br />
                              {globalText?.NO_STATISTICS_AVAILABLE?.split(" ")
                                .slice(
                                  -GlobalConfiguration?.lengthCheckSeven,
                                  globalText?.LENGTH_CHECK_ZERO,
                                )
                                .join(" ")}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FieldForceAccountDashboard;
